// Per-patient accent theming (D-33). Turns a patient's hex swatch into a small
// set of CSS custom properties so the chat bubbles, the active nav item, the
// onboarding tint, and the drug-level bars can all reflect "whose data is this"
// without hand-coding a Tailwind variant per swatch. Centralizes the two derived
// values legibility depends on: a darkened hover shade and a contrast-safe
// foreground color. Set the returned vars on a wrapper element; descendants then
// reference them via Tailwind's `bg-(--accent)` / `text-(--accent-contrast)`.

const DEFAULT_ACCENT = "#2563eb"; // blue-600 — fallback when no patient is active
const DEFAULT_ACCENT_HOVER = "#1d4ed8"; // blue-700

export interface PatientThemeVars {
  "--accent": string;
  "--accent-hover": string;
  "--accent-contrast": string;
}

function normalizeHex(hex: string): string | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  return m ? `#${m[1].toLowerCase()}` : null;
}

function toRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function toHex(r: number, g: number, b: number): string {
  const h = (v: number) =>
    Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
}

// WCAG relative luminance — used to pick a legible foreground over the swatch.
function luminance([r, g, b]: [number, number, number]): number {
  const lin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

export function patientTheme(color: string | undefined): PatientThemeVars {
  const hex = (color && normalizeHex(color)) || DEFAULT_ACCENT;
  const rgb = toRgb(hex);
  // The PATIENT_COLORS palette is curated dark enough for white text (matching the
  // white avatar initials); only fall back to dark text for an unusually light hue.
  const contrast = luminance(rgb) > 0.6 ? "#1f2937" /* gray-800 */ : "#ffffff";
  // Preserve the exact blue-700 hover for the default; otherwise darken the swatch.
  const hover =
    hex === DEFAULT_ACCENT
      ? DEFAULT_ACCENT_HOVER
      : toHex(rgb[0] * 0.88, rgb[1] * 0.88, rgb[2] * 0.88);
  return {
    "--accent": hex,
    "--accent-hover": hover,
    "--accent-contrast": contrast,
  };
}
